// Barrière de version de l'appli installée (logique pure ; portée de flowAI,
// src/lib/pwa-update.ts). Une appli installée peut rester ouverte des jours : sans signal, elle
// garde l'ancien code après un déploiement. La version est calculée au build ; le client la
// compare à /version.json (jamais en cache) et recharge, avec une garde anti-boucle.

/** sessionStorage : version pour laquelle on a déjà rechargé (garde anti-boucle). */
export const CLE_RECHARGEMENT = 'pmvb-version-rechargee'

/**
 * Recharger ? Seulement si le serveur annonce une version différente de la nôtre et qu'on n'a
 * pas déjà rechargé pour elle (un déploiement à moitié propagé bouclerait sinon).
 */
export function doitRecharger(
  courante: string | null | undefined,
  serveur: string | null | undefined,
  dejaTentee: string | null | undefined,
): boolean {
  if (!serveur || !courante) return false
  if (serveur === courante) return false
  if (serveur === dejaTentee) return false
  return true
}

/** Attente maximale d'un rechargement retenu par la seule file d'envoi (octets dans IndexedDB). */
export const PLAFOND_REPORT_MS = 15 * 60_000

/** Raisons dont le travail survit à un rechargement (le plafond peut passer outre). */
export const RAISONS_SURES: readonly string[] = ['file', 'attente-envoi']
/** Raisons qui ne retiennent le rechargement qu'un court moment (une requête en cours). */
export const RAISONS_CEDANTES: readonly string[] = ['envoi']

/**
 * Recharger maintenant ou attendre ? Aucune raison : maintenant. Une raison dont le travail est
 * en mémoire (pages choisies, raison inconnue, requête en cours) : attendre, quel que soit le
 * délai. Seulement des raisons sûres : attendre jusqu'au plafond, puis recharger.
 */
export function decisionRechargement(
  raisons: readonly string[],
  depuisMs: number,
  maintenantMs: number,
  plafondMs = PLAFOND_REPORT_MS,
): 'recharger' | 'attendre' {
  if (raisons.length === 0) return 'recharger'
  if (raisons.some((r) => !RAISONS_SURES.includes(r))) return 'attendre'
  return maintenantMs - depuisMs >= plafondMs ? 'recharger' : 'attendre'
}

/**
 * Au-delà du plafond, le rechargement n'est-il retenu que par une requête en cours (et des
 * raisons sûres) ? Alors on demande au draineur de céder entre deux tranches.
 */
export function doitDemanderFenetre(
  raisons: readonly string[],
  depuisMs: number,
  maintenantMs: number,
  plafondMs = PLAFOND_REPORT_MS,
): boolean {
  if (maintenantMs - depuisMs < plafondMs) return false
  if (!raisons.some((r) => RAISONS_CEDANTES.includes(r))) return false
  return raisons.every((r) => RAISONS_SURES.includes(r) || RAISONS_CEDANTES.includes(r))
}
